import { ICheckoutConfirmationPageContext } from "@src/interface/Checkout";
import { HeadFC, PageProps } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import CheckoutPending from "design-system/src/Core/CheckoutDetails/CheckoutPending/CheckoutPending";
import { useTranslation } from "@src/hooks/useTranslation";
import useAutoHideOrderTracking from "@src/layout/DefaultLayout/DefaultHeader/OrderTracking/useAutoHideOrderTracking";
import useAutoHideTopDeliveryStatus from "@src/store/reducers/CartSlice/hooks/useAutoHideTopDeliveryStatus";
import { PageContextIntl } from "@src/components/general/WrapPageElement/PageContext";
import DefaultHead from "@src/lib/Head/DefaultHead";
import ApiRequest from "@src/backbone/prototypes/ApiRequest";
import hasWindow from "@src/utils/hasWindow";

let refresh_timeout: NodeJS.Timeout;

const CHECKOUT_PENDING_REFRESH_DELAY = Number(
  process.env.GATSBY_CONF_CHECKOUT_PENDING_REFRESH_DELAY || 4
);
const CHECKOUT_PENDING_REFRESH_DELAY_WARN = Number(
  process.env.GATSBY_CONF_CHECKOUT_PENDING_REFRESH_DELAY_WARN || 30
);
const CheckoutPendingPage = (
  props: PageProps<any, ICheckoutConfirmationPageContext>
) => {
  const { t } = useTranslation(["Checkout", "UI"]);
  useAutoHideOrderTracking();
  useAutoHideTopDeliveryStatus();
  //
  const refresh_count = useRef<number>(0);
  const d0 = useRef<number>(new Date().getTime());
  const refresh_delay = CHECKOUT_PENDING_REFRESH_DELAY * 1000;
  const refresh_delay_warn = CHECKOUT_PENDING_REFRESH_DELAY_WARN * 1000;
  const [pendingMessage, setPendingMessage] = useState<string>(
    t(
      "Votre paiement est en cours de traitement, nous vous confirmerons votre commande dans quelques instants.",
      "Checkout"
    )
  );

  const RefreshPending = (
    delay: number,
    action: string,
    payment_action_id: string
  ) => {
    refresh_timeout && clearTimeout(refresh_timeout);
    return (refresh_timeout = setTimeout(function () {
      ApiRequest("OrderPending", null, {
        payment_action_id: payment_action_id,
        action: action,
      })
        .then((data) => {
          const { state } = data;
          switch (state) {
            case "error":
              return (window.location.href = "/checkout/error");
            case "confirmation":
              return (window.location.href = "/checkout/confirmation");
            default:
            case "pending":
              refresh_count.current++;
              RefreshPending(
                refresh_count.current * refresh_delay,
                action,
                payment_action_id
              );
              break;
          }
          const d = new Date().getTime();
          const refresh_total_time = d - d0.current;
          if (refresh_total_time < refresh_delay_warn) {
            setPendingMessage(
              t(
                "Cela ne devrait prendre plus que quelques instants…",
                "Checkout"
              )
            );
          } else {
            setPendingMessage(
              t(
                "Le traitement est plus long que prévu, nous attendons la confirmation de votre paiement…",
                "Checkout"
              )
            );
          }
        })
        .catch(function (error) {
          refresh_count.current++;
          RefreshPending(
            refresh_count.current * refresh_delay,
            action,
            payment_action_id
          );
        });
    }, delay));
  };

  useEffect(() => {
    if (hasWindow()) {
      const searchParams = new URLSearchParams(window.location.search),
        paymentId = searchParams.get("paymentId"),
        hostedCheckoutId = searchParams.get("hostedCheckoutId"),
        payment_action_id = paymentId || hostedCheckoutId || "",
        action = paymentId
          ? "paymentid"
          : hostedCheckoutId
          ? "hostedcheckoutid"
          : "";
      if (action) {
        RefreshPending(refresh_delay, action, payment_action_id);
      } else {
        setPendingMessage(t("Une erreur inconnue semble être apparue.", "UI"));
      }
    }
  }, []);

  const title = t("Veuillez patienter", "Checkout"),
    subtitle = t("votre paiement est en cours de traitement.", "Checkout"),
    description = pendingMessage,
    footer = t("Merci de ne pas quitter cette page.", "Checkout");
  return <CheckoutPending {...{ title, subtitle, description, footer }} />;
};

export default CheckoutPendingPage;

export const Head: HeadFC<any, PageContextIntl> = (headProps) => {
  return <DefaultHead {...headProps} />;
};
